import { createChart, LineSeries } from 'lightweight-charts'
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['container', 'sharedContainer', 'title', 'legend']
  static values = { data: Array }

  // Chart configuration constants
  static CHART_CONFIG = {
    colors: {
      revenue: '#2563eb',
      contractLiabilities: '#dc2626'
    },
    labels: {
      title: '營收與合約負債關係圖',
      revenue: '營收',
      contractLiabilities: '合約負債',
      period: '季度',
      unit: '千元'
    },
    defaultOptions: {
      layout: {
        background: { color: 'transparent' },
        textColor: '#333'
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' }
      },
      rightPriceScale: {
        borderColor: '#f0f0f0'
      },
      leftPriceScale: {
        visible: true,
        borderColor: '#f0f0f0'
      },
      timeScale: {
        borderColor: '#f0f0f0',
        timeVisible: false,
        secondsVisible: false,
        ticksVisible: true,
        rightOffset: 0,
        barSpacing: 6,
        fixLeftEdge: true,
        fixRightEdge: true,
        lockVisibleTimeRangeOnResize: true
      },
      crosshair: {
        mode: 1
      }
    }
  }

  connect() {
    if (this.hasValidData) {
      this.renderChart()
      this.renderSharedYAxisChart()
      this.setupResizeHandler()
    } else {
      this.renderNoDataMessage()
    }
  }

  disconnect() {
    if (this.chart) {
      this.chart.remove()
    }
    if (this.sharedYAxisChart) {
      this.sharedYAxisChart.remove()
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }

  get hasValidData() {
    return this.dataValue && Array.isArray(this.dataValue) && this.dataValue.length > 0
  }

  renderChart() {
    this.renderChartInContainer('container', 'chart', false)
  }

  renderSharedYAxisChart() {
    this.renderChartInContainer('sharedContainer', 'sharedYAxisChart', true)
  }

  renderChartInContainer(targetName, chartProperty, useSharedYAxis = false) {
    const target = this[`${targetName}Target`]
    const title = useSharedYAxis ? '營收與合約負債關係圖 (共用Y軸)' : '營收與合約負債關係圖'
    
    try {
      this.setupChartInTarget(target, chartProperty, title, useSharedYAxis)
      this.addSeriesToChart(this[chartProperty], useSharedYAxis)
      this.setupLegendForChart(target)
      this.setupCrosshairForChart(this[chartProperty], target, useSharedYAxis)
    } catch (error) {
      console.error(`Error rendering ${chartProperty}:`, error)
      this.renderErrorMessage(target, useSharedYAxis)
    }
  }

  renderNoDataMessage() {
    this.containerTarget.innerHTML = `
      <div class="flex items-center justify-center h-full text-gray-500">
        <p>暫無數據可顯示</p>
      </div>
    `
  }

  renderErrorMessage(target = this.containerTarget, isShared = false) {
    const message = isShared ? '共用Y軸圖表載入失敗，請稍後再試' : '圖表載入失敗，請稍後再試'
    target.innerHTML = `
      <div class="flex items-center justify-center h-full text-red-500">
        <p>${message}</p>
      </div>
    `
  }

  setupChartInTarget(target, chartProperty, title, useSharedYAxis = false) {
    const config = this.constructor.CHART_CONFIG
    const containerId = useSharedYAxis ? 'shared-chart-container' : 'chart-container'
    const legendId = useSharedYAxis ? 'shared-legend-container' : 'legend-container'
    const tooltipId = useSharedYAxis ? 'shared-tooltip' : 'tooltip'
    
    target.innerHTML = `
      <div class="mb-4">
        <h3 class="text-lg font-bold text-center">${title}</h3>
      </div>
      <div id="${containerId}" class="w-full relative" style="height: 400px;">
        <div id="${tooltipId}" class="absolute bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg pointer-events-none opacity-0 transition-opacity duration-200 z-10 whitespace-nowrap" style="transform: translate(-50%, -100%); margin-top: -8px;">
        </div>
      </div>
      <div id="${legendId}" class="flex justify-center mt-4 mb-6 space-x-6"></div>
    `

    const chartContainer = target.querySelector(`#${containerId}`)
    const chartOptions = {
      ...config.defaultOptions,
      width: this.getResponsiveWidth(),
      timeScale: {
        ...config.defaultOptions.timeScale,
        tickMarkFormatter: (time) => {
          const dataIndex = time - 1
          if (dataIndex >= 0 && dataIndex < this.dataValue.length) {
            return this.dataValue[dataIndex].period
          }
          return time.toString()
        }
      },
      localization: {
        priceFormatter: (price) => this.formatAxisNumber(price)
      }
    }

    if (useSharedYAxis) {
      chartOptions.leftPriceScale = { visible: false }
    }
    
    this[chartProperty] = createChart(chartContainer, chartOptions)
  }

  addSeriesToChart(chart, useSharedYAxis = false) {
    const config = this.constructor.CHART_CONFIG
    const { chartData, contractLiabilitiesData } = this.processChartData()

    // Configure price scale IDs based on chart type
    const revenueScaleId = useSharedYAxis ? 'right' : 'left'
    const contractLiabilitiesScaleId = 'right'

    // Add revenue line series
    const revenueSeries = chart.addSeries(LineSeries, {
      color: config.colors.revenue,
      lineWidth: 2,
      priceScaleId: revenueScaleId
    })
    
    revenueSeries.setData(chartData.filter(item => item.value !== null))

    // Add contract liabilities line series
    const contractLiabilitiesSeries = chart.addSeries(LineSeries, {
      color: config.colors.contractLiabilities,
      lineWidth: 2,
      priceScaleId: contractLiabilitiesScaleId
    })

    contractLiabilitiesSeries.setData(contractLiabilitiesData.filter(item => item.value !== null))

    // Configure price scales
    if (!useSharedYAxis) {
      chart.priceScale('left').applyOptions({
        scaleMargins: { top: 0.1, bottom: 0.1 }
      })
    }
    
    chart.priceScale('right').applyOptions({
      scaleMargins: { top: 0.1, bottom: 0.1 }
    })

    this.autoFitChart(chart)
  }

  autoFitChart(chart) {
    chart.timeScale().fitContent()
    
    if (this.dataValue.length > 0) {
      chart.timeScale().setVisibleRange({
        from: 1,
        to: this.dataValue.length
      })
    }
  }

  setupLegendForChart(target) {
    const config = this.constructor.CHART_CONFIG
    const isShared = target === this.sharedContainerTarget
    const legendId = isShared ? '#shared-legend-container' : '#legend-container'
    const legendContainer = target.querySelector(legendId)
    
    legendContainer.innerHTML = this.getLegendHTML(config)
  }

  getLegendHTML(config) {
    return `
      <div class="flex items-center">
        <div class="w-4 h-0.5 mr-2" style="background-color: ${config.colors.revenue}"></div>
        <span class="text-sm">${config.labels.revenue} (${config.labels.unit})</span>
      </div>
      <div class="flex items-center">
        <div class="w-4 h-0.5 mr-2" style="background-color: ${config.colors.contractLiabilities}"></div>
        <span class="text-sm">${config.labels.contractLiabilities} (${config.labels.unit})</span>
      </div>
    `
  }

  setupCrosshairForChart(chart, target, useSharedYAxis = false) {
    const tooltipId = useSharedYAxis ? '#shared-tooltip' : '#tooltip'
    const tooltip = target.querySelector(tooltipId)
    const chartContainer = target.querySelector(useSharedYAxis ? '#shared-chart-container' : '#chart-container')
    
    chart.subscribeCrosshairMove((param) => {
      this.handleCrosshairMoveForTarget(param, target, useSharedYAxis)
      this.handleTooltip(param, tooltip, chartContainer, useSharedYAxis)
    })
  }

  processChartData() {
    const chartData = []
    const contractLiabilitiesData = []

    this.dataValue.forEach((item, index) => {
      // Convert quarter data to a simple time format for lightweight-charts
      // Using index as simple time since lightweight-charts can handle both timestamps and simple indices
      const timePoint = index + 1
      
      if (item.revenue !== null && item.revenue !== undefined) {
        chartData.push({
          time: timePoint,
          value: item.revenue
        })
      }

      if (item.contract_liabilities !== null && item.contract_liabilities !== undefined) {
        contractLiabilitiesData.push({
          time: timePoint,
          value: item.contract_liabilities
        })
      }
    })

    return { chartData, contractLiabilitiesData }
  }

  handleCrosshairMoveForTarget(param, target, useSharedYAxis = false) {
    const legendId = useSharedYAxis ? '#shared-legend-container' : '#legend-container'
    const legendContainer = target.querySelector(legendId)
    
    if (!param.time || !param.point || !legendContainer) {
      this.resetLegendForTarget(target, useSharedYAxis)
      return
    }

    const dataIndex = param.time - 1
    if (dataIndex < 0 || dataIndex >= this.dataValue.length) {
      this.resetLegendForTarget(target, useSharedYAxis)
      return
    }

    const item = this.dataValue[dataIndex]
    const config = this.constructor.CHART_CONFIG
    
    legendContainer.innerHTML = this.getTooltipHTML(item, config)
  }

  getTooltipHTML(item, config) {
    return `
      <div class="text-sm font-medium text-center mb-2">${item.period}</div>
      <div class="flex justify-center space-x-6">
        <div class="flex items-center">
          <div class="w-4 h-0.5 mr-2" style="background-color: ${config.colors.revenue}"></div>
          <span class="text-sm">${config.labels.revenue}: ${this.formatNumber(item.revenue)} ${config.labels.unit}</span>
        </div>
        <div class="flex items-center">
          <div class="w-4 h-0.5 mr-2" style="background-color: ${config.colors.contractLiabilities}"></div>
          <span class="text-sm">${config.labels.contractLiabilities}: ${this.formatNumber(item.contract_liabilities)} ${config.labels.unit}</span>
        </div>
      </div>
    `
  }

  resetLegendForTarget(target, useSharedYAxis = false) {
    this.setupLegendForChart(target)
  }

  handleTooltip(param, tooltip, chartContainer, useSharedYAxis = false) {
    if (!param.point || !param.time || !tooltip) {
      this.hideTooltip(tooltip)
      return
    }

    const dataIndex = param.time - 1
    if (dataIndex < 0 || dataIndex >= this.dataValue.length) {
      this.hideTooltip(tooltip)
      return
    }

    const item = this.dataValue[dataIndex]
    const config = this.constructor.CHART_CONFIG
    
    // Always show both series data at the current x-axis position
    this.showBothSeriesTooltip(tooltip, param.point, chartContainer, item, config)
  }

  showBothSeriesTooltip(tooltip, point, chartContainer, item, config) {
    tooltip.innerHTML = `
      <div class="font-semibold mb-1 text-center">${item.period}</div>
      <div class="flex items-center mb-1">
        <div class="w-3 h-0.5 mr-2" style="background-color: ${config.colors.revenue}"></div>
        <span class="text-xs">${config.labels.revenue}: ${this.formatNumber(item.revenue)} ${config.labels.unit}</span>
      </div>
      <div class="flex items-center">
        <div class="w-3 h-0.5 mr-2" style="background-color: ${config.colors.contractLiabilities}"></div>
        <span class="text-xs">${config.labels.contractLiabilities}: ${this.formatNumber(item.contract_liabilities)} ${config.labels.unit}</span>
      </div>
    `
    
    tooltip.style.left = `${point.x}px`
    tooltip.style.top = `${point.y}px`
    tooltip.style.opacity = '1'
  }

  hideTooltip(tooltip) {
    tooltip.style.opacity = '0'
  }

  formatNumber(value) {
    if (value === null || value === undefined) {
      return 'N/A'
    }
    return new Intl.NumberFormat('zh-TW').format(value)
  }

  formatAxisNumber(price) {
    if (price === null || price === undefined) {
      return ''
    }
    
    // Round to integer and add thousands separator
    const roundedPrice = Math.round(price)
    return new Intl.NumberFormat('zh-TW', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedPrice)
  }

  getResponsiveWidth() {
    const containerWidth = this.containerTarget.clientWidth
    const screenWidth = window.innerWidth
    
    // Responsive width calculation for 768px ~ 1440px
    if (screenWidth < 768) {
      return Math.max(containerWidth, 320) // Minimum width for mobile
    } else if (screenWidth <= 1440) {
      // Use full container width within responsive range
      return Math.min(containerWidth, screenWidth - 40) // Leave some margin
    } else {
      // For screens larger than 1440px, cap the chart width
      return Math.min(containerWidth, 1400)
    }
  }


  setupResizeHandler() {
    const DEBOUNCE_DELAY = 150
    let resizeTimeout
    
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        this.handleResize()
      }, DEBOUNCE_DELAY)
    }
    
    window.addEventListener('resize', this.resizeHandler)
  }

  handleResize() {
    const newWidth = this.getResponsiveWidth()
    const charts = [this.chart, this.sharedYAxisChart].filter(Boolean)
    
    charts.forEach(chart => {
      chart.applyOptions({ width: newWidth })
    })
  }
}